import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';

import { useIntersection, useMeasure, useScroll } from 'react-use';

import { arrayOfLength, calcProgress, clamp, trimDecimals } from '@pkg/utils';

type RefCallback = (instance: HTMLDivElement | null) => void;

const MIN_SCROLL_THRESHOLD = 16;

// The problem this hook solves:
// Track width + horizontal scroll distance of the containing
// <DataTable /> in order to guess what columns are
// "scrolled behind" the first sticky column. There are many
// alternate ways to solve this problem more accurately, but
// this solution achieves what we want it to most of the time.

// Additional context:
// <DataTable /> uses HTML <table /> markup + layout.
// There are several complexities to solve for our design,
// mainly: rounded corners + sticky elements + scrolling.
// Rows/cells needs to have a (semi-)transparent background.
// Without a solid background, we will see the content
// "scrolling behind" the first (sticky) column. This hook
// solves the problem by allowing us to update the `opacity`
// of a column when it crosses a certain scroll threshold.

export function useFadableColumns(separated = false) {
    const clonedRef = useRef<HTMLDivElement | null>(null);

    // Not all of these hooks use a debounce / rAF limiter.
    // Therefor, we can end up with some very agressive re-renders
    // when: resizing / updating layout / content changes / etc.
    const [setRefState, { width }] = useMeasure<HTMLDivElement>();
    const intersection = useIntersection(clonedRef, {});
    const { x: scrollLeft } = useScroll(clonedRef);

    const isIntersecting = Boolean(intersection?.isIntersecting);

    const [scrollRange, setScrollRange] = useState(0);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [overflowCount, setOverflowCount] = useState(0);
    const [progressPerCol, setProgressPerCol] = useState(100);

    useEffect(() => {
        if (separated || !clonedRef.current || !isIntersecting || !width) return;

        const { scrollWidth } = clonedRef.current;
        const scrollRange = scrollWidth - width;
        setScrollRange(scrollRange);

        // Too little scrollable distance to apply "fade styles".
        if (scrollRange < MIN_SCROLL_THRESHOLD) return;

        const columnCount = clonedRef.current.querySelector('thead tr')?.childElementCount ?? 1;
        setProgressPerCol(100 / columnCount);

        // Nothing to fade if only a single column.
        if (columnCount <= 1) return;

        const averageWidth = scrollWidth / columnCount;
        setOverflowCount(Math.ceil(scrollRange / averageWidth) + 1);
    }, [separated, width, isIntersecting]);

    useEffect(() => {
        if (separated || !isIntersecting || !scrollRange) return;
        setScrollProgress(calcProgress(scrollLeft, { max: scrollRange, round: true }));
    }, [separated, isIntersecting, scrollLeft, scrollRange]);

    const cssCustomProps = useMemo(() => {
        if (separated || scrollRange < MIN_SCROLL_THRESHOLD) return {};

        return arrayOfLength(overflowCount).reduce<CSSProperties>((collection, index) => {
            const indexOffset = index + 1;
            const cssPropName = `--cell-opacity-col-${index + 2}`;

            const previousProgress = index ? clamp(0, (scrollProgress / (progressPerCol * index)) * 100, 100) : 0;

            const currentPreference = (scrollProgress / (progressPerCol * indexOffset)) * 100;
            const currentProgress = clamp(0, currentPreference - previousProgress, 100);

            const opacity = trimDecimals(Math.abs(currentProgress / 100 - 1));

            return {
                ...collection,
                [cssPropName]: opacity,
            };
        }, {});
    }, [separated, scrollRange, overflowCount, scrollProgress, progressPerCol]);

    const handleSetRef: RefCallback = (element) => {
        if (!element) return;

        // `react-use` hooks do not share a common `ref` interface.
        // Therefore, we need to manage our own ref separately.
        clonedRef.current = element;
        setRefState(element);
    };

    return {
        cssCustomProps,
        handleSetRef,
    };
}
